import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Layout, Card, Table, Button, Select, Space, Typography, Row, Col, Statistic,
  Spin, Empty, Tag, DatePicker, Alert, message,
} from 'antd';
import {
  PrinterOutlined, SafetyCertificateOutlined, ArrowLeftOutlined, FileTextOutlined,
  TeamOutlined, DollarOutlined, FilePdfOutlined, FileExcelOutlined, UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import API from '../config/api';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import { useClinicContext } from './ClinicContext';
import { useDoctorContext } from './DoctorContext';
import { ClinicDoctorOption, getClinicDoctorOptions } from '../utils/clinicDoctors';
import { calculateEffectiveCoveragePercentage } from '../utils/insuranceCoverage';
import { normalizeInsuranceCollection } from '../utils/insuranceCollection';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface PackageInfo {
  package_name: string;
  total_sessions: number;
  remaining_sessions: number;
  used_sessions: number;
  status: 'active' | 'finished' | 'expired';
}

interface PatientRow {
  patient_id: string;
  patient_name: string;
  file_number: string;
  insurance_number: string;
  date: string;
  visit_type: string;
  full_fee: number;
  patient_paid: number;
  patient_collected: number;
  patient_remaining: number;
  company_owes: number;
  coverage_percentage: number;
  doctor_id: string;
  doctor_name: string;
  packages?: PackageInfo[];
}

interface CompanyGroup {
  company_id: string;
  company_key: string;
  company_name: string;
  coverage_percentage: number;
  patients: PatientRow[];
  total_full_fee: number;
  total_patient_paid: number;
  total_patient_collected: number;
  total_patient_remaining: number;
  total_company_owes: number;
  total_count: number;
}

interface ReportData {
  companies: CompanyGroup[];
  totals: {
    total_patients: number;
    total_full_fee: number;
    total_patient_paid: number;
    total_patient_collected: number;
    total_patient_remaining: number;
    total_company_owes: number;
  };
}

const companyKey = (name: string, id: string) => (name || id || 'unknown').trim().toLocaleLowerCase('ar');

function calculateTotals(companies: CompanyGroup[]) {
  return {
    total_patients: companies.reduce((sum, group) => sum + group.total_count, 0),
    total_full_fee: companies.reduce((sum, group) => sum + group.total_full_fee, 0),
    total_patient_paid: companies.reduce((sum, group) => sum + group.total_patient_paid, 0),
    total_patient_collected: companies.reduce((sum, group) => sum + group.total_patient_collected, 0),
    total_patient_remaining: companies.reduce((sum, group) => sum + group.total_patient_remaining, 0),
    total_company_owes: companies.reduce((sum, group) => sum + group.total_company_owes, 0),
  };
}

function mergeDoctorReports(entries: Array<{ doctor: ClinicDoctorOption; data: any }>): ReportData {
  const groups = new Map<string, CompanyGroup>();
  entries.forEach(({ doctor, data }) => {
    (data?.companies || []).forEach((source: any) => {
      const key = companyKey(source.company_name, source.company_id);
      const target: CompanyGroup = groups.get(key) || {
        company_id: source.company_id || key,
        company_key: key,
        company_name: source.company_name || 'غير محدد',
        coverage_percentage: Number(source.coverage_percentage) || 0,
        patients: [], total_full_fee: 0, total_patient_paid: 0,
        total_patient_collected: 0, total_patient_remaining: 0,
        total_company_owes: 0, total_count: 0,
      };
      const rows: PatientRow[] = (source.patients || []).map((patient: any) => {
        const collection = normalizeInsuranceCollection(
          patient.patient_paid,
          patient.patient_collected,
          patient.patient_remaining,
        );
        return {
          ...patient,
          patient_collected: collection.collected,
          patient_remaining: collection.remaining,
          coverage_percentage: calculateEffectiveCoveragePercentage(
            patient.full_fee,
            patient.company_owes,
            patient.coverage_percentage,
          ),
          doctor_id: doctor.id,
          doctor_name: doctor.name,
        };
      });
      target.patients.push(...rows);
      target.total_full_fee += Number(source.total_full_fee) || 0;
      target.total_patient_paid += Number(source.total_patient_paid) || 0;
      target.total_patient_collected += rows.reduce((sum, row) => sum + row.patient_collected, 0);
      target.total_patient_remaining += rows.reduce((sum, row) => sum + row.patient_remaining, 0);
      target.total_company_owes += Number(source.total_company_owes) || 0;
      target.total_count += Number(source.total_count) || rows.length;
      groups.set(key, target);
    });
  });
  const companies = Array.from(groups.values()).sort((a, b) => a.company_name.localeCompare(b.company_name, 'ar'));
  companies.forEach((group) => {
    group.patients.sort((a, b) => a.date.localeCompare(b.date));
    group.coverage_percentage = calculateEffectiveCoveragePercentage(
      group.total_full_fee,
      group.total_company_owes,
      group.coverage_percentage,
    );
  });
  return { companies, totals: calculateTotals(companies) };
}

const InsuranceReport: React.FC = () => {
  const [rawReport, setRawReport] = useState<ReportData | null>(null);
  const [doctors, setDoctors] = useState<ClinicDoctorOption[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs()]);
  const [selectedDoctorId, setSelectedDoctorId] = useState('all');
  const [selectedCompanyKey, setSelectedCompanyKey] = useState('all');
  const printRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { selectedClinicId, selectedClinic } = useClinicContext();
  const { doctorId, doctorName } = useDoctorContext();
  const baseUrl = API.BASE_URL;

  useEffect(() => {
    let cancelled = false;
    setDoctorsLoading(true);
    getClinicDoctorOptions(selectedClinicId, doctorId, doctorName)
      .then((items) => {
        if (cancelled) return;
        setDoctors(items);
        setSelectedDoctorId((current) => current === 'all' || items.some((item) => item.id === current) ? current : 'all');
      })
      .finally(() => !cancelled && setDoctorsLoading(false));
    return () => { cancelled = true; };
  }, [selectedClinicId, doctorId, doctorName]);

  const fetchReport = useCallback(async () => {
    const targets = selectedDoctorId === 'all' ? doctors : doctors.filter((doctor) => doctor.id === selectedDoctorId);
    if (!targets.length) {
      setRawReport(null);
      return;
    }
    setLoading(true);
    try {
      const params = {
        date_from: dateRange[0].format('YYYY-MM-DD'),
        date_to: dateRange[1].format('YYYY-MM-DD'),
      };
      const results = await Promise.allSettled(targets.map(async (doctor) => {
        const response = await axios.get(`${baseUrl}${API.ENDPOINTS.INSURANCE_REPORT(doctor.id)}`, { params });
        return { doctor, data: response.data?.data || null };
      }));
      const successful = results
        .filter((result): result is PromiseFulfilledResult<{ doctor: ClinicDoctorOption; data: any }> => result.status === 'fulfilled')
        .map((result) => result.value);
      const failedCount = results.length - successful.length;
      setRawReport(mergeDoctorReports(successful));
      if (failedCount > 0) message.warning(`تعذر تحميل تقرير ${failedCount} طبيب. تم عرض البيانات المتاحة فقط.`);
    } catch {
      setRawReport(null);
      message.error('تعذر تحميل تقرير التأمين.');
    } finally {
      setLoading(false);
    }
  }, [baseUrl, dateRange, doctors, selectedDoctorId]);

  useEffect(() => { if (!doctorsLoading) fetchReport(); }, [doctorsLoading, fetchReport]);

  const reportData = useMemo<ReportData | null>(() => {
    if (!rawReport) return null;
    const companies = selectedCompanyKey === 'all'
      ? rawReport.companies
      : rawReport.companies.filter((group) => group.company_key === selectedCompanyKey);
    return { companies, totals: calculateTotals(companies) };
  }, [rawReport, selectedCompanyKey]);

  useEffect(() => {
    if (selectedCompanyKey !== 'all' && !rawReport?.companies.some((group) => group.company_key === selectedCompanyKey)) {
      setSelectedCompanyKey('all');
    }
  }, [rawReport, selectedCompanyKey]);

  const selectedDoctorLabel = selectedDoctorId === 'all'
    ? 'جميع الأطباء'
    : doctors.find((doctor) => doctor.id === selectedDoctorId)?.name || 'الطبيب';
  const selectedCompanyLabel = selectedCompanyKey === 'all'
    ? 'جميع شركات التأمين'
    : rawReport?.companies.find((group) => group.company_key === selectedCompanyKey)?.company_name || 'شركة التأمين';
  const hasData = Boolean(reportData?.companies.length);
  const fileSuffix = `${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}`;

  const exportExcel = () => {
    if (!reportData || !hasData) return;
    const rows = reportData.companies.flatMap((group) => group.patients.map((patient) => ({
      'الطبيب': patient.doctor_name,
      'شركة التأمين': group.company_name,
      'اسم المريض': patient.patient_name,
      'رقم الملف': patient.file_number || '',
      'رقم التأمين': patient.insurance_number || '',
      'التاريخ': patient.date,
      'نوع الزيارة': patient.visit_type,
      'الإجمالي': patient.full_fee,
      'على المريض': patient.patient_paid,
      'دفع حتى الآن': patient.patient_collected,
      'المتبقي على المريض': patient.patient_remaining,
      'على الشركة': patient.company_owes,
      'نسبة التغطية الفعلية': `${patient.coverage_percentage}%`,
      'الباقات والجلسات': (patient.packages || []).map((item) => `${item.package_name}: ${item.used_sessions}/${item.total_sessions}`).join(' | '),
    })));
    const summary = [{
      'الفترة': `${dateRange[0].format('YYYY-MM-DD')} - ${dateRange[1].format('YYYY-MM-DD')}`,
      'الأطباء': selectedDoctorLabel,
      'الشركات': selectedCompanyLabel,
      'عدد المرضى': reportData.totals.total_patients,
      'إجمالي الفواتير': reportData.totals.total_full_fee,
      'إجمالي على المرضى': reportData.totals.total_patient_paid,
      'المحصل من المرضى': reportData.totals.total_patient_collected,
      'المتبقي على المرضى': reportData.totals.total_patient_remaining,
      'على الشركات': reportData.totals.total_company_owes,
    }];
    const workbook = XLSX.utils.book_new();
    const detailsSheet = XLSX.utils.json_to_sheet(rows);
    detailsSheet['!cols'] = [
      { wch: 22 }, { wch: 22 }, { wch: 24 }, { wch: 13 }, { wch: 16 },
      { wch: 13 }, { wch: 18 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
      { wch: 16 }, { wch: 20 }, { wch: 14 }, { wch: 20 }, { wch: 38 },
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summary);
    summarySheet['!cols'] = Array.from({ length: 9 }, () => ({ wch: 22 }));
    XLSX.utils.book_append_sheet(workbook, detailsSheet, 'Insurance report');
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    XLSX.writeFile(workbook, `insurance-report_${fileSuffix}.xlsx`);
    message.success('تم تنزيل ملف Excel.');
  };

  const exportPdf = async () => {
    if (!printRef.current || !hasData) return;
    setPdfLoading(true);
    printRef.current.classList.add('pdf-exporting');
    try {
      const html2pdfModule = await import('html2pdf.js');
      await html2pdfModule.default().set({
        margin: 5,
        filename: `insurance-report_${fileSuffix}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, windowWidth: 1600 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      }).from(printRef.current).save();
      message.success('تم تنزيل ملف PDF.');
    } catch (error) {
      console.error('Insurance PDF export failed:', error);
      message.error('تعذر إنشاء ملف PDF.');
    } finally {
      printRef.current?.classList.remove('pdf-exporting');
      setPdfLoading(false);
    }
  };

  const patientColumns = [
    { title: 'الطبيب', dataIndex: 'doctor_name', key: 'doctor_name', render: (text: string) => <Tag icon={<UserOutlined />}>{text}</Tag> },
    { title: 'اسم المريض', dataIndex: 'patient_name', key: 'patient_name', render: (text: string) => <strong>{text}</strong> },
    { title: 'رقم الملف', dataIndex: 'file_number', key: 'file_number', render: (text: string) => text || <Text type="secondary">—</Text> },
    { title: 'رقم التأمين', dataIndex: 'insurance_number', key: 'insurance_number', render: (text: string) => text || <Text type="secondary">—</Text> },
    { title: 'التاريخ', dataIndex: 'date', key: 'date' },
    { title: 'نوع الزيارة', dataIndex: 'visit_type', key: 'visit_type' },
    { title: 'الإجمالي', dataIndex: 'full_fee', key: 'full_fee', render: (value: number) => `${value.toLocaleString()} ج` },
    { title: 'على المريض', dataIndex: 'patient_paid', key: 'patient_paid', render: (value: number) => <Tag color="cyan">{value.toLocaleString()} ج</Tag> },
    { title: 'دفع حتى الآن', dataIndex: 'patient_collected', key: 'patient_collected', render: (value: number) => <Tag color="green">{value.toLocaleString()} ج</Tag> },
    { title: 'المتبقي على المريض', dataIndex: 'patient_remaining', key: 'patient_remaining', render: (value: number) => <Tag color={value > 0 ? 'orange' : 'default'}>{value.toLocaleString()} ج</Tag> },
    { title: 'على الشركة', dataIndex: 'company_owes', key: 'company_owes', render: (value: number) => <Tag color="blue">{value.toLocaleString()} ج</Tag> },
    {
      title: 'نسبة التغطية', dataIndex: 'coverage_percentage', key: 'coverage_percentage',
      render: (value: number) => <Tag color="cyan">{value.toLocaleString()}%</Tag>,
    },
    {
      title: 'الباقة / الجلسات', dataIndex: 'packages', key: 'packages',
      render: (packages: PackageInfo[] | undefined) => !packages?.length ? <Text type="secondary">—</Text> : (
        <Space direction="vertical" size={2}>{packages.map((item, index) => <Tag key={`${item.package_name}-${index}`} color={item.status === 'active' ? 'blue' : item.status === 'expired' ? 'red' : 'default'}>{item.package_name}: {item.used_sessions} من {item.total_sessions}</Tag>)}</Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', direction: 'rtl' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout>
        <DashboardHeader onSettingsClick={() => navigate('/settings')} onMenuClick={() => {}} />
        <Content style={{ padding: 24, background: '#f5f5f5' }}>
          <div className="no-print">
            <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col>
                <Space>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')}>رجوع</Button>
                  <Title level={3} style={{ margin: 0 }}><SafetyCertificateOutlined /> تقارير التأمين</Title>
                  {selectedClinic?.name && <Tag color="blue">{selectedClinic.name}</Tag>}
                </Space>
              </Col>
              <Col>
                <Space wrap>
                  <RangePicker value={dateRange} onChange={(values) => values?.[0] && values?.[1] && setDateRange([values[0], values[1]])} format="YYYY-MM-DD" allowClear={false}
                    presets={[
                      { label: 'هذا الشهر', value: [dayjs().startOf('month'), dayjs()] },
                      { label: 'الشهر الماضي', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                      { label: 'آخر 30 يوم', value: [dayjs().subtract(29, 'days'), dayjs()] },
                      { label: 'هذا العام', value: [dayjs().startOf('year'), dayjs()] },
                    ]}
                  />
                  <Select loading={doctorsLoading} value={selectedDoctorId} onChange={setSelectedDoctorId} style={{ width: 210 }} suffixIcon={<UserOutlined />}>
                    <Option value="all">جميع الأطباء</Option>
                    {doctors.map((doctor) => <Option key={doctor.id} value={doctor.id}>{doctor.name}</Option>)}
                  </Select>
                  <Select value={selectedCompanyKey} onChange={setSelectedCompanyKey} style={{ width: 210 }}>
                    <Option value="all">جميع شركات التأمين</Option>
                    {(rawReport?.companies || []).map((group) => <Option key={group.company_key} value={group.company_key}>{group.company_name}</Option>)}
                  </Select>
                </Space>
              </Col>
            </Row>

            <Card size="small" style={{ marginBottom: 20 }}>
              <Row justify="space-between" align="middle" gutter={[12, 12]}>
                <Col><Text type="secondary">التقرير الحالي: </Text><Text strong>{selectedDoctorLabel} — {selectedCompanyLabel}</Text></Col>
                <Col><Space wrap>
                  <Button icon={<PrinterOutlined />} onClick={() => window.print()} disabled={!hasData}>طباعة</Button>
                  <Button icon={<FilePdfOutlined />} danger loading={pdfLoading} onClick={exportPdf} disabled={!hasData}>تصدير PDF</Button>
                  <Button icon={<FileExcelOutlined />} style={{ color: '#15803d', borderColor: '#15803d' }} onClick={exportExcel} disabled={!hasData}>تصدير Excel</Button>
                </Space></Col>
              </Row>
            </Card>
          </div>

          {selectedClinicId === '__all_clinics__' && <Alert type="info" showIcon message="اختر عيادة محددة من أعلى الصفحة لعرض جميع أطبائها." style={{ marginBottom: 16 }} />}

          <div ref={printRef} id="insurance-report-print">
            <div className="print-only insurance-export-header">
              <Title level={2}>تقرير التأمين</Title>
              <Text>{selectedClinic?.name || ''} — {selectedDoctorLabel}</Text><br />
              <Text>{selectedCompanyLabel} — من {dateRange[0].format('YYYY-MM-DD')} إلى {dateRange[1].format('YYYY-MM-DD')}</Text>
            </div>

            {loading || doctorsLoading ? <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div> : !hasData ? (
              <Empty description={`لا توجد بيانات تأمين من ${dateRange[0].format('YYYY-MM-DD')} إلى ${dateRange[1].format('YYYY-MM-DD')}`} />
            ) : <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}><Card><Statistic title="إجمالي مرضى التأمين" value={reportData!.totals.total_patients} prefix={<TeamOutlined />} suffix="مريض" /></Card></Col>
                <Col xs={24} sm={12} lg={6}><Card><Statistic title="إجمالي الفواتير" value={reportData!.totals.total_full_fee} suffix="ج" valueStyle={{ color: '#1890ff' }} prefix={<DollarOutlined />} /></Card></Col>
                <Col xs={24} sm={12} lg={6}><Card><Statistic title="إجمالي على المرضى" value={reportData!.totals.total_patient_paid} suffix="ج" valueStyle={{ color: '#13c2c2' }} prefix={<DollarOutlined />} /></Card></Col>
                <Col xs={24} sm={12} lg={6}><Card><Statistic title="على الشركات" value={reportData!.totals.total_company_owes} suffix="ج" valueStyle={{ color: '#fa8c16' }} prefix={<DollarOutlined />} /></Card></Col>
              </Row>

              {reportData!.companies.map((group) => <Card key={group.company_key} className="insurance-company-report-card" style={{ marginBottom: 24 }}
                title={<Row justify="space-between" gutter={[8, 8]}><Col><FileTextOutlined /> <strong>{group.company_name}</strong></Col><Col><Text type="secondary">{group.total_count} مريض | الإجمالي {group.total_full_fee.toLocaleString()} ج | على الشركة {group.total_company_owes.toLocaleString()} ج</Text></Col></Row>}
              >
                <Table dataSource={group.patients} columns={patientColumns} rowKey={(row, index) => `${row.doctor_id}-${row.patient_id}-${row.date}-${index}`} pagination={false} size="small" bordered scroll={{ x: 1450 }}
                  summary={() => <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0} colSpan={6}>الإجمالي</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{group.total_full_fee.toLocaleString()} ج</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}><Tag color="cyan">{group.total_patient_paid.toLocaleString()} ج</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={8}><Tag color="green">{group.total_patient_collected.toLocaleString()} ج</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={9}><Tag color="orange">{group.total_patient_remaining.toLocaleString()} ج</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={10}><Tag color="blue">{group.total_company_owes.toLocaleString()} ج</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={11} />
                    <Table.Summary.Cell index={12} />
                  </Table.Summary.Row>}
                />
              </Card>)}
            </>}
          </div>
        </Content>
      </Layout>

      <style>{`
        .print-only { display: none; }
        .pdf-exporting { direction: rtl; background: white; padding: 12px; }
        .pdf-exporting .print-only { display: block !important; }
        .pdf-exporting .ant-table-content { overflow: visible !important; }
        .insurance-export-header { text-align: center; margin-bottom: 22px; }
        .insurance-company-report-card { break-inside: avoid; page-break-inside: avoid; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .ant-layout-sider, .ant-layout-header { display: none !important; }
          body { background: white !important; }
          #insurance-report-print { padding: 0 !important; }
        }
      `}</style>
    </Layout>
  );
};

export default InsuranceReport;
